import { useState, useEffect } from "react";
import api from "@/lib/api";
import AnalysisModal from "@/components/AnalysisModal";
import { X, TrendingUp, ExternalLink, Newspaper } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const SHOWN_KEY = "fas_news_popup_last";
const THREE_H = 3 * 60 * 60 * 1000;

export default function NewsPopup() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [ticker, setTicker] = useState(null);

  const loadAndShow = async () => {
    try {
      const res = await api.get("/news/highlights");
      const list = res.data?.items || [];
      if (list.length) {
        setItems(list);
        setOpen(true);
        localStorage.setItem(SHOWN_KEY, String(Date.now()));
      }
    } catch (e) {}
  };

  useEffect(() => {
    const last = parseInt(localStorage.getItem(SHOWN_KEY) || "0", 10);
    const due = Date.now() - last > THREE_H;
    const t = setTimeout(() => { if (due) loadAndShow(); }, 2200);
    const iv = setInterval(loadAndShow, THREE_H); // update & tampil ulang tiap 3 jam
    return () => { clearTimeout(t); clearInterval(iv); };
  }, []);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.96 }}
          transition={{ type: "spring", stiffness: 260, damping: 24 }}
          className="fixed bottom-4 right-4 z-[70] w-[calc(100vw-2rem)] sm:w-[400px] card p-0 overflow-hidden shadow-2xl border-[var(--bull)]/30"
          data-testid="news-popup"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-[var(--bull)]/10 border-b border-[var(--bull)]/20">
            <span className="inline-flex items-center gap-2 font-heading font-bold text-sm">
              <span className="w-7 h-7 rounded-lg bg-[var(--bull)]/15 flex items-center justify-center"><TrendingUp size={15} className="text-bull" /></span>
              Sentimen Positif · Saham ID
            </span>
            <button onClick={() => setOpen(false)} data-testid="news-popup-close" className="p-1.5 rounded-md hover:bg-[var(--surface-hover)] text-[var(--text-muted)]"><X size={16} /></button>
          </div>
          <div className="p-3 space-y-2">
            {items.slice(0, 3).map((n, i) => (
              <a
                key={i}
                href={n.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`news-popup-item-${i}`}
                className="block p-2.5 rounded-lg bg-[var(--surface-base)] border border-[var(--border-subtle)] hover:border-[var(--bull)]/30 transition-colors group"
              >
                <div className="flex items-start gap-2">
                  <span className="mt-0.5 w-5 h-5 rounded-md bg-[var(--bull)]/15 text-bull flex items-center justify-center text-[10px] font-bold shrink-0">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold leading-snug line-clamp-2 group-hover:text-accent transition-colors">{n.title}</p>
                    {n.impact && <p className="text-[11px] text-bull mt-1 flex items-start gap-1"><TrendingUp size={11} className="mt-0.5 shrink-0" /> {n.impact}</p>}
                    <div className="flex items-center gap-2 mt-1 text-[10px] text-[var(--text-muted)] flex-wrap">
                      <span>{n.source}</span>
                      {n.tickers?.length > 0 && (
                        <span className="flex items-center gap-1">
                          {n.tickers.slice(0, 3).map((tk) => (
                            <button
                              key={tk}
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTicker(String(tk).toUpperCase()); }}
                              data-testid={`news-popup-ticker-${tk}`}
                              className="px-1.5 py-0.5 rounded bg-[var(--accent)]/15 text-accent font-bold hover:bg-[var(--accent)]/25 transition-colors"
                              title={`Analisis AI ${tk}`}
                            >
                              {tk}
                            </button>
                          ))}
                        </span>
                      )}
                      <ExternalLink size={10} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
          <div className="px-4 py-2 border-t border-[var(--border-subtle)] flex items-center gap-1.5 text-[10px] text-[var(--text-muted)]">
            <Newspaper size={11} /> Diperbarui otomatis tiap 3 jam · edukasi, DYOR.
          </div>
        </motion.div>
      )}
      {ticker && <AnalysisModal ticker={ticker} onClose={() => setTicker(null)} />}
    </AnimatePresence>
  );
}
